


// --- IMPORTS ---
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PortfolioPosition } from '../portfolio/model/PortfolioPosition';
import { rebuildViewUtil } from './rebuildViewUtil';
import { normalizeStoreOnchainAsset, normalizeStoreOnchainAssets } from '../features/onchain/storeOnchainAsset';

// --- TYPES & INTERFACES ---
export type InsightType = 'RISK' | 'OPPORTUNITY' | 'INFO';
export interface PortfolioInsight {
  type: InsightType;
  title: string;
  implication: string;
  actions: string[];
  priority: number;
}

export interface ManualPortfolioPosition {
  id: string;
  assetId: string;
  quantity: number;
  price?: number;
  sourceType: 'MANUAL';
}

// --- STORE ---
export const usePortfolioStore = create<any>()(
  persist(
    (set: any, get: any) => ({
      positions: [],
      exchangesPositions: [],
      manualPositions: [],
      onchainAssets: [],
      totalValue: 0,
      allocation: [],
      stressTests: [],
      riskAnalysis: [],
      insights: [],
      // Supprime un wallet on-chain par id unique
      removeOnchainWallet: (id: string) => {
        console.log('[Store] removeOnchainWallet called with id:', id);
        set((state: any) => {
          const before = state.onchainAssets || [];
          const after = Array.isArray(before)
            ? before.filter((w: any) => String(w?.id) !== String(id) && String(w?.walletId) !== String(id))
            : [];
          console.log('[Store] onchainAssets before:', before);
          console.log('[Store] onchainAssets after:', after);
          return {
            ...state,
            onchainAssets: after
          };
        });
        get().rebuildView && get().rebuildView();
      },
      addOnchainAsset: (asset: any) => {
        const normalizedAsset = normalizeStoreOnchainAsset(asset);
        set((state: any) => {
          const before = state.onchainAssets || [];
          const after = Array.isArray(before) ? [...before, normalizedAsset] : [normalizedAsset];
          console.log('[Store] addOnchainAsset called. Before:', before);
          console.log('[Store] addOnchainAsset. Adding:', normalizedAsset);
          console.log('[Store] addOnchainAsset. After:', after);
          return {
            ...state,
            onchainAssets: after
          };
        });
        get().rebuildView && get().rebuildView();
      },
      updateOnchainAsset: (asset: any) => {
        const normalizedAsset = normalizeStoreOnchainAsset(asset);
        set((state: any) => {
          const before = normalizeStoreOnchainAssets(state.onchainAssets || []);
          let updated = false;
          const after = before.map((entry: any) => {
            const sameId = normalizedAsset.id != null && entry.id != null && String(entry.id) === String(normalizedAsset.id);
            const sameWalletAsset = normalizedAsset.walletId != null
              && entry.walletId != null
              && String(entry.walletId) === String(normalizedAsset.walletId)
              && String(entry.assetId) === String(normalizedAsset.assetId);

            if (sameId || sameWalletAsset) {
              updated = true;
              return { ...entry, ...normalizedAsset };
            }
            return entry;
          });

          return {
            ...state,
            onchainAssets: updated ? after : [...before, normalizedAsset]
          };
        });
        get().rebuildView && get().rebuildView();
      },
      rebuildView: () => rebuildViewUtil(get, set),
      applySyncResult: (rawPortfolio: PortfolioPosition[]) => {
        if (!rawPortfolio || rawPortfolio.length === 0) return;
        const sourceId = rawPortfolio[0].sourceId;
        set((state: any) => {
          const prev = Array.isArray(state.exchangesPositions) ? state.exchangesPositions : [];
          // On ne met à jour que les positions de l'exchange concerné, on garde les autres inchangées
          const updated = prev.filter((p: PortfolioPosition) => p.sourceId !== sourceId);
          // Ajoute ou remplace les positions de l'exchange rafraîchi
          rawPortfolio.forEach((pos: PortfolioPosition) => {
            updated.push({ ...pos });
          });
          // Met aussi à jour positions (toutes les positions d'exchange)
          const allPositions = updated.filter((p: PortfolioPosition) => {
            const st = String(p.sourceType);
            return st !== 'MANUAL' && st !== 'ONCHAIN';
          });
          return { ...state, exchangesPositions: updated, positions: allPositions };
        });
        get().rebuildView();
      },
      applyDiff: (diff: Partial<PortfolioPosition>[]) => {
        set((state: any) => ({
          ...state,
          positions: state.positions.map((pos: PortfolioPosition) => {
            const d = diff.find((df: Partial<PortfolioPosition>) => df.assetId === pos.assetId);
            return d ? { ...pos, ...d } : pos;
          })
        }));
        get().rebuildView();
      },
      clearPortfolio: () => {
        set((state: any) => ({ ...state, positions: [], totalValue: 0, allocation: [] }));
      },
      addManualPosition: (position: any) => {
        set((state: any) => ({
          ...state,
          manualPositions: Array.isArray(state.manualPositions)
            ? [...state.manualPositions, position]
            : [position]
        }));
        get().rebuildView && get().rebuildView();
      },
      updateManualPosition: (position: any) => {
        set((state: any) => {
          let updated = false;
          const manualPositions = Array.isArray(state.manualPositions) ? state.manualPositions.map((p: any) => {
            if (p.assetId === position.assetId) {
              updated = true;
              return { ...p, ...position };
            }
            return p;
          }) : [];
          return {
            ...state,
            manualPositions: updated
              ? manualPositions
              : [...(Array.isArray(state.manualPositions) ? state.manualPositions : []), position]
          };
        });
        get().rebuildView && get().rebuildView();
      },
      removeManualPosition: (id: string) => {
        set((state: any) => ({
          ...state,
          manualPositions: Array.isArray(state.manualPositions)
            ? state.manualPositions.filter((p: any) => String(p?.id) !== String(id))
            : []
        }));
        get().rebuildView && get().rebuildView();
      },
            // Vide tous les wallets on-chain
      clearOnchainAssets: () => {
        set((state: any) => ({
          ...state,
          onchainAssets: []
        }));
        // Remove persisted onchainAssets from localStorage (Zustand persist)
        try {
          localStorage.removeItem('portfolio-store');
        } catch (e) {}
        get().rebuildView && get().rebuildView();
      },
      // Remplace tous les wallets on-chain par une nouvelle liste (utilisé pour synchroniser avec le backend)
      setOnchainAssets: (assets: any[]) => {
        set((state: any) => ({
          ...state,
          onchainAssets: normalizeStoreOnchainAssets(assets)
        }));
        get().rebuildView && get().rebuildView();
      },
      getUnifiedPositions: () => {
        const state = get();
        const positions = Array.isArray(state.positions) ? state.positions : [];
        const manualPositions = Array.isArray(state.manualPositions) ? state.manualPositions : [];
        // Utilise toujours onchainAssets du store
        const onchainAssets = normalizeStoreOnchainAssets(state.onchainAssets);
        return [
          ...positions.map((p: any) => ({ ...p, sourceType: p.sourceType || 'EXCHANGE' })),
          ...manualPositions.map((p: any) => ({ ...p, type: 'MANUAL' })),
          ...onchainAssets.map((p: any) => ({ ...p, sourceType: 'ONCHAIN', type: 'ONCHAIN' }))
        ];
      }
    }),
    {
      name: 'portfolio-store',
      storage: {
        getItem: (name: string) => {
          const value = localStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name: string, value: any) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name: string) => localStorage.removeItem(name)
      }
    }
  )
);
